// Iter 122 — Hub landing pages: Clinical, Knowledge, Operations.
// Tile grid pulled from the central navRegistry so adding a destination
// automatically surfaces it on its parent hub.
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import { SECTIONS } from '../lib/sectionTheme';
import { destinationsForHub } from '../lib/navRegistry';
import { usePermissions } from '../context/PermissionsContext';
import { useAuth } from '../context/AuthContext';

function HubLanding({ hubId, accent, title, subtitle }) {
  const { user } = useAuth();
  const isPM = user?.role === 'admin' || user?.role === 'superuser';
  const { canDestination, loaded } = usePermissions();
  // First narrow by the registry's role: 'all' | 'pm' filter (legacy),
  // then by the permissions system (granted destinations).
  const baseTiles = destinationsForHub(hubId, isPM);
  const tiles = loaded ? baseTiles.filter(t => canDestination(t.id)) : baseTiles;

  return (
    <div className="min-h-screen bg-background" data-testid={`hub-${hubId}`}>
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-8">
          <div className="text-xs font-bold tracking-[0.18em] uppercase mb-2"
               style={{ color: accent.color }}>
            {accent.label.toUpperCase()}
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight" style={{ color: '#0d2644' }}>
            {title}
          </h1>
          <p className="text-base text-muted-foreground mt-2 max-w-2xl">{subtitle}</p>
        </div>

        {tiles.length === 0 && (
          <div className="text-center py-16 border rounded-xl bg-card text-muted-foreground">
            Nothing in this hub for your account yet.
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {tiles.map(t => {
            const Icon = t.icon;
            return (
              <Link key={t.id} to={t.href} data-testid={`hub-tile-${t.id}`}
                className="group relative rounded-xl border bg-card p-5 transition-all hover:shadow-md hover:-translate-y-0.5">
                <div className="flex items-start gap-3">
                  <div className="rounded-lg p-2 shrink-0"
                       style={{ background: accent.colorBg, color: accent.color }}>
                    <Icon className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h3 className="font-bold text-base" style={{ color: '#0d2644' }}>
                        {t.label}
                      </h3>
                      <ArrowRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                                  style={{ color: accent.color }} />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                      {t.blurb}
                    </p>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function ClinicalHub() {
  return <HubLanding hubId="clinical" accent={SECTIONS.clinical}
    title="Clinical workflows"
    subtitle="Everything patient-facing — consent, anticoagulant decisions, and procedure prep." />;
}

export function KnowledgeHub() {
  return <HubLanding hubId="knowledge" accent={SECTIONS.knowledge}
    title="Knowledge & reference"
    subtitle="The practice's brain — SOPs, training modules, MBS rules, and your referrer directory." />;
}

export function OperationsHub() {
  return <HubLanding hubId="operations" accent={SECTIONS.operations}
    title="Practice operations"
    subtitle="Run the day-to-day — rosters, equipment, your team, and outbound communications." />;
}
